import { useCallback, type RefObject } from "react"

import { nanoid } from "nanoid"

import { TRACKING_EVENTS, type useAnalytics } from "@/lib/analytics"
import { nextAvailableCopyName } from "@/utils/nextAvailableCopyName"

import type {
  DashboardWidget,
  DashboardWidgetConfig,
  WidgetTemplate,
} from "../types/core"
import {
  insertWidget,
  moveWidget,
  removeWidget,
  setWidgetWidth,
  type WidgetMoveTarget,
} from "../utils/dashboardFlowMoves"

type TrackEvent = ReturnType<typeof useAnalytics>["trackEvent"]

export function useDashboardWidgetActions({
  currentDashboardId,
  currentWidgetsRef,
  enqueueDashboardSave,
  safeEnqueueDashboardSave,
  trackEvent,
}: {
  currentDashboardId: string | undefined
  currentWidgetsRef: RefObject<Record<string, DashboardWidget[]>>
  enqueueDashboardSave: (
    dashboardId: string,
    widgets: DashboardWidget[],
  ) => Promise<void>
  safeEnqueueDashboardSave: (
    dashboardId: string,
    widgets: DashboardWidget[],
  ) => void
  trackEvent: TrackEvent
}) {
  const handleAddWidget = useCallback(
    async (template: WidgetTemplate, config: DashboardWidgetConfig) => {
      const dashboardId = currentDashboardId

      if (!dashboardId) {
        return null
      }

      const currentWidgets = currentWidgetsRef.current[dashboardId] ?? []
      const newWidget: DashboardWidget = {
        id: nanoid(),
        type: template.type,
        title: config.title || template.title,
        config,
      }

      const updatedWidgets = insertWidget(currentWidgets, newWidget)
      await enqueueDashboardSave(dashboardId, updatedWidgets)

      const addedWidget = updatedWidgets.find(
        (widget) => widget.id === newWidget.id,
      )
      trackEvent(TRACKING_EVENTS.DASHBOARD.WIDGET_ADDED, {
        widget_id: newWidget.id,
        widget_type: newWidget.type,
        dashboard_id: dashboardId,
        position: { x: addedWidget?.grid_x, y: addedWidget?.grid_y },
      })

      return newWidget.id
    },
    [currentDashboardId, currentWidgetsRef, enqueueDashboardSave, trackEvent],
  )

  const handleRemoveWidget = useCallback(
    (id: string) => {
      const dashboardId = currentDashboardId

      if (!dashboardId) {
        return
      }

      const currentWidgets = currentWidgetsRef.current[dashboardId] ?? []
      safeEnqueueDashboardSave(dashboardId, removeWidget(currentWidgets, id))
    },
    [currentDashboardId, currentWidgetsRef, safeEnqueueDashboardSave],
  )

  const handleCloneWidget = useCallback(
    (id: string) => {
      const dashboardId = currentDashboardId

      if (!dashboardId) {
        return
      }

      const currentWidgets = currentWidgetsRef.current[dashboardId] ?? []
      const widgetToClone = currentWidgets.find(
        (widget: DashboardWidget) => widget.id === id,
      )
      if (!widgetToClone) {
        return
      }

      const sourceTitle = widgetToClone.config?.title || widgetToClone.title
      const takenTitles = new Set(
        currentWidgets.map(
          (widget: DashboardWidget) => widget.config?.title || widget.title,
        ),
      )
      const clonedTitle = nextAvailableCopyName(sourceTitle, takenTitles)
      const clonedWidget: DashboardWidget = {
        ...widgetToClone,
        id: nanoid(),
        title: clonedTitle,
        config: widgetToClone.config
          ? { ...widgetToClone.config, title: clonedTitle }
          : widgetToClone.config,
      }

      const updatedWidgets = insertWidget(currentWidgets, clonedWidget, id)
      safeEnqueueDashboardSave(dashboardId, updatedWidgets)

      const placedClone = updatedWidgets.find(
        (widget) => widget.id === clonedWidget.id,
      )
      trackEvent(TRACKING_EVENTS.DASHBOARD.WIDGET_ADDED, {
        widget_id: clonedWidget.id,
        widget_type: clonedWidget.type,
        dashboard_id: dashboardId,
        position: { x: placedClone?.grid_x, y: placedClone?.grid_y },
        cloned_from: id,
      })
    },
    [
      currentDashboardId,
      currentWidgetsRef,
      safeEnqueueDashboardSave,
      trackEvent,
    ],
  )

  const handleUpdateWidget = useCallback(
    async (id: string, config: DashboardWidgetConfig) => {
      const dashboardId = currentDashboardId

      if (!dashboardId) {
        return
      }

      const currentWidgets = currentWidgetsRef.current[dashboardId] ?? []
      const updatedWidgets = currentWidgets.map((widget: DashboardWidget) => {
        if (widget.id === id) {
          const updatedWidget = { ...widget, config }
          if (config.title !== undefined) {
            updatedWidget.title = config.title
          }
          return updatedWidget
        }
        return widget
      })

      await enqueueDashboardSave(dashboardId, updatedWidgets)

      const widget = currentWidgets.find((entry) => entry.id === id)
      if (widget) {
        trackEvent(TRACKING_EVENTS.DASHBOARD.WIDGET_CONFIGURED, {
          widget_id: id,
          widget_type: widget.type,
          configuration_changes: config,
        })
      }
    },
    [currentDashboardId, currentWidgetsRef, enqueueDashboardSave, trackEvent],
  )

  const handleClearDashboard = useCallback(() => {
    const dashboardId = currentDashboardId

    if (!dashboardId) {
      return
    }

    safeEnqueueDashboardSave(dashboardId, [])
  }, [currentDashboardId, safeEnqueueDashboardSave])

  const handleWidgetMove = useCallback(
    (activeWidgetId: string, target: WidgetMoveTarget) => {
      const dashboardId = currentDashboardId

      if (!dashboardId) {
        return
      }

      const currentWidgets = currentWidgetsRef.current[dashboardId] ?? []
      const updatedWidgets = moveWidget(currentWidgets, activeWidgetId, target)
      if (updatedWidgets === currentWidgets) {
        return
      }

      safeEnqueueDashboardSave(dashboardId, updatedWidgets)

      trackEvent(TRACKING_EVENTS.DASHBOARD.GRID_LAYOUT_CHANGED, {
        dashboard_id: dashboardId,
        widget_count: currentWidgets.length,
        change_type: `move_to_${target.kind}`,
        widget_id: activeWidgetId,
      })
    },
    [
      currentDashboardId,
      currentWidgetsRef,
      safeEnqueueDashboardSave,
      trackEvent,
    ],
  )

  const handleWidgetWidthChange = useCallback(
    (widgetId: string, columns: number) => {
      const dashboardId = currentDashboardId

      if (!dashboardId) {
        return
      }

      const currentWidgets = currentWidgetsRef.current[dashboardId] ?? []
      safeEnqueueDashboardSave(
        dashboardId,
        setWidgetWidth(currentWidgets, widgetId, columns),
      )

      trackEvent(TRACKING_EVENTS.DASHBOARD.GRID_LAYOUT_CHANGED, {
        dashboard_id: dashboardId,
        widget_count: currentWidgets.length,
        change_type: "width",
        widget_id: widgetId,
        widget_columns: columns,
      })
    },
    [
      currentDashboardId,
      currentWidgetsRef,
      safeEnqueueDashboardSave,
      trackEvent,
    ],
  )

  return {
    handleAddWidget,
    handleClearDashboard,
    handleCloneWidget,
    handleRemoveWidget,
    handleUpdateWidget,
    handleWidgetMove,
    handleWidgetWidthChange,
  }
}
